import LinkIcon from '@mui/icons-material/LinkRounded'
import { IconButton, Tooltip, Typography, styled } from '@mui/material'
import { memo, useCallback } from 'react'
import { useIntl } from 'react-intl'
import { colorPalette } from '../../../../style/colorPalette'
import { StaleIndicatorIcon } from '../../../../components/icons/StaleIndicatorIcon'

type Props = {
  path?: string
  pathStale?: boolean
  parentDisplayName?: string
  code?: string
}

export const ProjectPlanPath = memo(
  ({ path, pathStale, parentDisplayName, code }: Props) => {
    const intl = useIntl()
    const handleCopyUrl = useCallback(() => {
      if (!code) return
      const url = `${window.location.origin}/wbsItem/${code}`
      navigator.clipboard.writeText(url)
    }, [code])

    const pathTooltip =
      path ?? intl.formatMessage({ id: 'wbsItemPath.calculating' })

    return (
      <StyledWrapper>
        <StyledCodeArea>
          {code && <StyledCode>{code}</StyledCode>}
          {code && (
            <Tooltip
              title={intl.formatMessage({ id: 'copyUrl' })}
              placement="top"
            >
              <StyledCopyButton
                onClick={handleCopyUrl}
                size="small"
                data-no-dnd="true"
              >
                <LinkIcon sx={{ fontSize: 14, transform: 'rotate(-45deg)' }} />
              </StyledCopyButton>
            </Tooltip>
          )}
        </StyledCodeArea>
        <StyledPathArea>
          <Tooltip title={pathTooltip} placement="top">
            <StyledContainer>
              <StyledPrefix>... /</StyledPrefix>
              <Typography
                component="div"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {parentDisplayName}
              </Typography>
              <StyledSuffix>/</StyledSuffix>
            </StyledContainer>
          </Tooltip>
          {pathStale && (
            <StaleIndicatorIcon
              message={intl.formatMessage({ id: 'viewModel.stale.path' })}
            />
          )}
        </StyledPathArea>
      </StyledWrapper>
    )
  }
)

const StyledWrapper = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: '8px',
  width: '100%',
})

const StyledPathArea = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '4px',
  minWidth: 0,
})

const StyledContainer = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  minWidth: 0,
  fontWeight: '400',
  color: colorPalette.monotone[4],
})

const StyledPrefix = styled('p')({
  margin: '0 5px 0 0',
  flexShrink: 0,
})

const StyledSuffix = styled('p')({
  margin: '0 0 0 5px',
  flexShrink: 0,
})

const StyledCode = styled('div')({
  flexShrink: 0,
  whiteSpace: 'nowrap',
  fontWeight: '600',
  fontSize: '11px',
  color: colorPalette.monotone[10],
})

const StyledCodeArea = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '2px',
  flexShrink: 0,
})

const StyledCopyButton = styled(IconButton)({
  padding: '2px',
  color: colorPalette.monotone[4],
  '&:hover': {
    backgroundColor: colorPalette.monotone[2],
  },
})
